import React, { useEffect, useState } from 'react'
import { CreditCard, Package, HardDrive, Zap, User, Building } from 'lucide-react'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { useUser } from '@/context/UserContext'
import AIActionLogsViewer from './AIActionLogsViewer'
import { 
  useSubscriptionStore,
  useUpgradeSubscription,
  useUpgradeOrganizationSubscription,
  usePurchasePackage 
} from '@/stores/subscriptionStore'
import { useGetOrganizations } from '@/stores/organizationsStore'

interface SubscriptionManagerProps {
  organizationId?: string
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ organizationId }) => {
  const user = useUser()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState(organizationId ? 'organization' : 'personal')
  const [selectedOrgId, setSelectedOrgId] = useState<string>(organizationId || '')
  
  // Get user's organizations for selection
  const {
    organizations,
    fetchOrganizations,
  } = useGetOrganizations()

  // Debug logging
  console.log('SubscriptionManager props:', { organizationId })
  console.log('User from context:', { userId: user?.id, userOrgId: user?.organizationId })
    useEffect(() => {
    console.log('SubscriptionManager - organizationId prop:', organizationId)
    console.log('SubscriptionManager - user context:', { userId: user?.id, userOrgId: user?.organizationId })
    
    // Fetch organizations to see available orgs
    if (user?.id) {
      fetchOrganizations()
    }
    
    // Set initial selectedOrgId if not already set
    if (!selectedOrgId && (organizationId || user?.organizationId)) {
      setSelectedOrgId(organizationId || user?.organizationId || '')
    }
  }, [organizationId, user, fetchOrganizations, selectedOrgId])
  useEffect(() => {
    console.log('Available organizations:', organizations)
    
    // Auto-select organization if not already selected
    if (organizations && organizations.length > 0 && !selectedOrgId) {
      // Prefer organizationId prop, then user.organizationId, then first available org
      const defaultOrgId = organizationId || user?.organizationId || organizations[0]?.id
      if (defaultOrgId) {
        setSelectedOrgId(defaultOrgId)
      }
    }
  }, [organizations, organizationId, user?.organizationId, selectedOrgId])// Handle tab changes and ensure we don't stay on organization tab without organizationId
  const handleTabChange = (value: string) => {
    const effectiveOrgId = organizationId || user?.organizationId || selectedOrgId || (organizations && organizations.length > 0 ? organizations[0]?.id : null)
    if (value === 'organization' && !effectiveOrgId) {
      // Don't allow switching to organization tab if no organizationId
      return
    }
    setActiveTab(value)    
    // If switching to organization tab and we have an effective org ID, set it as selected
    if (value === 'organization' && effectiveOrgId && !selectedOrgId) {
      setSelectedOrgId(effectiveOrgId)
    }
  }
    const {
    getPersonalSubscription,
    getOrganizationSubscription,
    subscriptionLoading,
    subscriptionError,
    packages,
    packagesLoading,
    fetchSubscriptionInfo,
    fetchOrganizationSubscription,
    fetchActionPackages,
  } = useSubscriptionStore()
  
  // Get current subscription based on active tab and organization selection
  const getCurrentSubscription = () => {
    if (activeTab === 'organization' && selectedOrgId) {
      return getOrganizationSubscription(selectedOrgId)
    } else if (activeTab === 'personal') {
      return getPersonalSubscription()
    }
    return null
  }
    const subscriptionInfo = getCurrentSubscription()
  
  const upgradeSubscription = useUpgradeSubscription()
  const upgradeOrganizationSubscription = useUpgradeOrganizationSubscription()
  const purchasePackage = usePurchasePackage()
  
  useEffect(() => {
    if (user?.id) {
      // Use organizationId prop, user.organizationId, or selectedOrgId, whichever is available
      const effectiveOrgId = organizationId || user?.organizationId || selectedOrgId
      
      console.log('Subscription fetch effect:', {
        userId: user.id,
        activeTab,
        organizationId,
        userOrgId: user.organizationId,
        selectedOrgId,
        effectiveOrgId
      })
      
      if (activeTab === 'organization' && effectiveOrgId) {
        console.log('Fetching organization subscription for:', effectiveOrgId)
        fetchOrganizationSubscription(effectiveOrgId)
      } else if (activeTab === 'personal') {
        console.log('Fetching personal subscription')
        fetchSubscriptionInfo()
      }
      fetchActionPackages()
    }
  }, [user?.id, activeTab, organizationId, selectedOrgId, fetchSubscriptionInfo, fetchOrganizationSubscription, fetchActionPackages])
  const getUsagePercentage = (used: number, limit: number) => {
    return limit > 0 ? Math.min((used / limit) * 100, 100) : 0
  }

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600'
    if (percentage >= 75) return 'text-orange-600'
    return 'text-green-600'
  }

  // Safe accessors for subscription data
  const safeUsage = subscriptionInfo?.usage || { 
    currentPeriod: { aiActions: 0, documents: 0, projects: 0, storageUsed: 0 }, 
    periodStart: new Date().toISOString(), 
    periodEnd: new Date().toISOString() 
  }
  const safeLimits = subscriptionInfo?.limits || { 
    aiActionsPerMonth: 0, 
    documentsPerMonth: 0, 
    storageGB: 0,
    projectsTotal: 0,
    collaboratorsPerProject: 0,
    prioritySupport: false,    customTemplates: false,
    advancedAnalytics: false,
    apiAccess: false
  }
  const safeCurrentPeriod = safeUsage?.currentPeriod || { aiActions: 0, documents: 0, projects: 0, storageUsed: 0 }
  const safeTier = subscriptionInfo?.tier || (activeTab === 'organization' ? 'NO_TIER' : 'FREE')
  const safeStatus = subscriptionInfo?.status || 'inactive'
  const safePackages = Array.isArray(packages) ? packages : []
  
  const getTierDisplayName = (tier: string) => {
    const tierNames: Record<string, string> = {
      'FREE': 'Free',
      'NO_TIER': 'No Subscription',
      'BASIC': 'Starter',
      'PRO': 'Professional',
      'PAY_AS_YOU_GO': 'Pay-as-you-go',
      'BUSINESS': 'Business',
      'BUSINESS_PRO': 'Business Pro',
      'HOBBYIST': 'Hobbyist',
      'STUDENT': 'Student',
      'ENTERPRISE': 'Enterprise'    }
    return tierNames[tier] || tier
  }

  const getPlans = () => {    // Return different plans based on whether viewing personal or organization subscription
    if (activeTab === 'organization') {
      return [
        {
          name: 'No Subscription',
          price: '$0',
          tier: 'NO_TIER',
          features: ['0 AI actions/month', '0 active projects', '0 GB storage', 'No support', 'All features restricted'],
        },
        {
          name: 'Business',
          price: '$99',
          tier: 'BUSINESS',
          features: ['1,000 AI actions/month', '10 active projects', '20 GB storage', 'Team collaboration', 'Priority support'],
        },
        {
          name: 'Business Pro',
          price: '$299',
          tier: 'BUSINESS_PRO',
          features: ['10,000 AI actions/month', '50 active projects', '100 GB storage', 'Advanced team features', 'API access', 'Phone support'],
        },
        {
          name: 'Enterprise',
          price: '$999',
          tier: 'ENTERPRISE',
          features: ['Unlimited AI actions', 'Unlimited active projects', 'Unlimited storage', 'Custom integrations', 'Dedicated support', 'SLA guarantees'],        },
      ]
    } else {
      return [
        {
          name: 'Free',
          price: '$0',
          tier: 'FREE',
          features: ['50 AI actions/month', '2 active projects', '0.1 GB storage', 'Basic support'],
        },
        {
          name: 'Starter',
          price: '$19',
          tier: 'BASIC',
          features: ['200 AI actions/month', '5 active projects', '1 GB storage', 'Email support'],
        },
        {
          name: 'Professional',
          price: '$49',
          tier: 'PRO',
          features: ['1000 AI actions/month', '25 active projects', '5 GB storage', 'Priority support', 'Custom templates'],
        },
        {
          name: 'Pay-as-you-go',
          price: '$199',
          tier: 'PAY_AS_YOU_GO',
          features: ['Buy actions as needed (20,000 included in package)', 'Unlimited active projects', 'Unlimited storage', 'Phone support', 'API access', '30 days per agent/month'],
        },
      ]
    }
  }

  const plans = getPlans()
  const refreshSubscriptionData = () => {
    if (user?.id) {
      const effectiveOrgId = organizationId || user?.organizationId || selectedOrgId
      if (activeTab === 'organization' && effectiveOrgId) {
        fetchOrganizationSubscription(effectiveOrgId)
      } else {
        fetchSubscriptionInfo()
      }
    }
  }

  // Additional safety check - if any of the safe accessors are still undefined, show loading
  if (!safeCurrentPeriod || !safeLimits) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }  const handleUpgrade = async (newTier: string) => {
    if (!user?.id) return

    const effectiveOrgId = organizationId || user?.organizationId || selectedOrgId

    console.log('handleUpgrade called:', {
      newTier,
      activeTab,
      organizationId,
      userOrgId: user?.organizationId,
      selectedOrgId,
      effectiveOrgId
    })

    if (activeTab === 'organization' && effectiveOrgId) {
      // Upgrade organization subscription
      console.log('Upgrading organization subscription with organizationId:', effectiveOrgId)
      upgradeOrganizationSubscription.execute({
        data: {
          organizationId: effectiveOrgId,
          tier: newTier,
        },
        onSuccess: () => {
          toast({
            title: 'Organization Subscription Upgraded',
            description: `Successfully upgraded organization to ${getTierDisplayName(newTier)} plan`,
          })
          // Refresh subscription data to show updated plan with a small delay
          console.log('Upgrading organization subscription, refreshing data...')
          setTimeout(() => {
            refreshSubscriptionData()
          }, 500) // 500ms delay to ensure backend has processed the update
        },
        onError: (error) => {
          toast({
            title: 'Organization Upgrade Failed',
            description: error.message || 'Failed to upgrade organization subscription',
            variant: 'destructive',
          })
        },
      })
    } else {
      // Upgrade personal subscription
      console.log('Upgrading personal subscription for userId:', user.id)
      upgradeSubscription.execute({
        data: {
          userId: user.id,
          tier: newTier,
        },
        onSuccess: () => {
          toast({
            title: 'Subscription Upgraded',
            description: `Successfully upgraded to ${getTierDisplayName(newTier)} plan`,
          })
          // Refresh subscription data to show updated plan with a small delay
          console.log('Upgrading personal subscription, refreshing data...')
          setTimeout(() => {
            refreshSubscriptionData()
          }, 500) // 500ms delay to ensure backend has processed the update
        },
        onError: (error) => {
          toast({
            title: 'Upgrade Failed',
            description: error.message,
            variant: 'destructive',
          })
        },
      })
    }
  }

  const handlePurchasePackage = async (packageId: string) => {
    if (!user?.id) return

    purchasePackage.execute({
      data: {
        userId: user.id,
        packageId,
        organizationId,
      },
      onSuccess: () => {
        toast({
          title: 'Package Purchased',
          description: 'Action package has been added to your account',
        })
        fetchActionPackages()
        refreshSubscriptionData()
      },
      onError: (error) => {
        toast({
          title: 'Purchase Failed',
          description: error.message,
          variant: 'destructive',
        })
      },
    })
  }
  if (subscriptionLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (subscriptionError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading subscription information: {subscriptionError}</p>
        <Button 
          onClick={refreshSubscriptionData}
          className="mt-4"
        >
          Retry
        </Button>
      </div>
    )
  }

  if (!subscriptionInfo) {
    return (
      <div className="text-center py-8">
        <p>No subscription information available</p>
        <Button 
          onClick={refreshSubscriptionData}
          className="mt-4"
        >
          Retry
        </Button>
      </div>
    )  }

  const renderSubscriptionContent = () => {
    // Handle organization without subscription
    if (activeTab === 'organization' && safeTier === 'NO_TIER') {
      return (
        <div className="space-y-6">
          {/* No Subscription indicator */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Building className="h-5 w-5 text-orange-600" />
              <span className="font-medium text-orange-900">No Organization Subscription</span>
            </div>
            <p className="text-sm text-orange-700 mt-1">
              This organization doesn't have an active subscription. All features are restricted until a subscription is activated.
            </p>
          </div>

          {/* Current Status Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
              <CardTitle className="text-sm font-medium">Current Status</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">No Subscription</div>
              <p className="text-xs text-muted-foreground">
                Status: <Badge variant="destructive">Inactive</Badge>
              </p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Building className="h-3 w-3" />
                Organization needs subscription to access features
              </p>
            </CardContent>
          </Card>

          {/* Restrictions Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
                <CardTitle className="text-sm font-medium">AI Actions</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">0</div>
                <p className="text-xs text-muted-foreground">No actions allowed</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
                <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">0</div>
                <p className="text-xs text-muted-foreground">No projects allowed</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
                <CardTitle className="text-sm font-medium">Storage</CardTitle>
                <HardDrive className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">0 GB</div>
                <p className="text-xs text-muted-foreground">No storage allowed</p>
              </CardContent>
            </Card>
          </div>

          {/* Available Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card key={plan.tier} className="flex flex-col">
                <CardHeader className="" hasCardAction={false}>
                  <CardTitle className="flex items-center justify-between">
                    {plan.name}
                    <Badge variant="outline">{plan.price}</Badge>
                  </CardTitle>
                  <CardDescription>
                    Get started with business features
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="space-y-2 text-sm">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>                  <Button 
                    className="w-full" 
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={upgradeSubscription.loading}
                  >
                    {upgradeSubscription.loading ? 'Processing...' : 'Get Started'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      )
    }

    // Normal subscription content for personal or organization with subscription
    return (
      <div className="space-y-6">
        {/* Context indicator for organization vs personal */}
        {activeTab === 'organization' && organizationId && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Building className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-900">Organization Subscription</span>
            </div>
            <p className="text-sm text-blue-700 mt-1">
              This subscription applies to your entire organization and all its members.
            </p>
          </div>
        )}
        
        {activeTab === 'personal' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-900">Personal Subscription</span>
            </div>
            <p className="text-sm text-green-700 mt-1">
              This subscription applies only to your personal account.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{/* Current Plan Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
            <CardTitle className="text-sm font-medium">Current Plan</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>          <CardContent>
            <div className="text-2xl font-bold">{getTierDisplayName(safeTier)}</div>
            <p className="text-xs text-muted-foreground">
              Status: <Badge variant={safeStatus === 'active' ? 'default' : 'secondary'}>
                {safeStatus}
              </Badge>
            </p>            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              {subscriptionInfo?.isOrganizationSubscription ? (
                <>
                  <Building className="h-3 w-3" />
                  Organization Subscription{subscriptionInfo.organizationId ? ` (${subscriptionInfo.organizationId})` : ''}
                </>
              ) : (
                <>
                  <User className="h-3 w-3" />
                  Personal Subscription
                </>
              )}
            </p>
            {subscriptionInfo?.currentPeriodStart && subscriptionInfo?.currentPeriodEnd && (
              <p className="text-xs text-muted-foreground mt-1">
                Billing Period: {new Date(subscriptionInfo.currentPeriodStart).toLocaleDateString()} - {new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>        {/* AI Actions Usage */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
            <CardTitle className="text-sm font-medium">AI Actions</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>          <CardContent>            <div className="text-2xl font-bold">
              {(safeCurrentPeriod?.aiActions || 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              of {(safeLimits?.aiActionsPerMonth || 0).toLocaleString()} this month
            </p>
            <Progress 
              value={getUsagePercentage(safeCurrentPeriod?.aiActions || 0, safeLimits?.aiActionsPerMonth || 0)} 
              className="mt-2"
            />
          </CardContent>
        </Card>        {/* Active Projects Usage */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(safeCurrentPeriod?.projects || 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              of {(safeLimits?.projectsTotal || 0).toLocaleString()} maximum
            </p>
            <Progress 
              value={getUsagePercentage(safeCurrentPeriod?.projects || 0, safeLimits?.projectsTotal || 0)} 
              className="mt-2"
            />
          </CardContent>
        </Card>{/* Storage Usage */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" hasCardAction={false}>
            <CardTitle className="text-sm font-medium">Storage</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>          <CardContent>            <div className="text-2xl font-bold">
              {((safeCurrentPeriod?.storageUsed || 0) / 1024).toFixed(1)} GB
            </div>
            <p className="text-xs text-muted-foreground">
              of {safeLimits?.storageGB || 0} GB
            </p>
            <Progress 
              value={getUsagePercentage((safeCurrentPeriod?.storageUsed || 0) / 1024, safeLimits?.storageGB || 0)} 
              className="mt-2"
            />
          </CardContent>
        </Card>
      </div>      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="upgrade">Upgrade Plan</TabsTrigger>
          <TabsTrigger value="packages">Action Packages</TabsTrigger>
          <TabsTrigger value="action-logs">AI Action Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">          <Card>
            <CardHeader className="" hasCardAction={false}>
              <CardTitle>Usage Summary</CardTitle>              <CardDescription>
                Current period: {new Date(safeUsage.periodStart).toLocaleDateString()} - {new Date(safeUsage.periodEnd).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">                <div>
                  <div className="flex justify-between text-sm">
                    <span>AI Actions</span>                    <span className={getUsageColor(getUsagePercentage(safeCurrentPeriod?.aiActions || 0, safeLimits?.aiActionsPerMonth || 0))}>
                      {safeCurrentPeriod?.aiActions || 0} / {safeLimits?.aiActionsPerMonth || 0}
                    </span>
                  </div>
                  <Progress 
                    value={getUsagePercentage(safeCurrentPeriod?.aiActions || 0, safeLimits?.aiActionsPerMonth || 0)} 
                    className="mt-1"
                  />
                </div>                <div>
                  <div className="flex justify-between text-sm">
                    <span>Active Projects</span>
                    <span className={getUsageColor(getUsagePercentage(safeCurrentPeriod?.projects || 0, safeLimits?.projectsTotal || 0))}>
                      {safeCurrentPeriod?.projects || 0} / {safeLimits?.projectsTotal || 0}
                    </span>
                  </div>
                  <Progress 
                    value={getUsagePercentage(safeCurrentPeriod?.projects || 0, safeLimits?.projectsTotal || 0)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <div className="flex justify-between text-sm">
                    <span>Storage</span>
                    <span className={getUsageColor(getUsagePercentage((safeCurrentPeriod?.storageUsed || 0) / 1024, safeLimits?.storageGB || 0))}>
                      {((safeCurrentPeriod?.storageUsed || 0) / 1024).toFixed(1)} GB / {safeLimits?.storageGB || 0} GB
                    </span>
                  </div>
                  <Progress 
                    value={getUsagePercentage((safeCurrentPeriod?.storageUsed || 0) / 1024, safeLimits?.storageGB || 0)} 
                    className="mt-1"
                  />
                </div>
              </div>              <div className="pt-4 border-t">
                <h3 className="font-medium mb-2">Features Available</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">                  <div className="flex items-center">
                    <Badge variant={safeLimits?.prioritySupport ? 'default' : 'secondary'} className="mr-2">
                      {safeLimits?.prioritySupport ? '✓' : '✗'}
                    </Badge>
                    Priority Support
                  </div>
                  <div className="flex items-center">                    <Badge variant={safeLimits?.customTemplates ? 'default' : 'secondary'} className="mr-2">
                      {safeLimits?.customTemplates ? '✓' : '✗'}
                    </Badge>
                    Custom Templates
                  </div>
                  <div className="flex items-center">                    <Badge variant={safeLimits?.advancedAnalytics ? 'default' : 'secondary'} className="mr-2">
                      {safeLimits?.advancedAnalytics ? '✓' : '✗'}
                    </Badge>
                    Advanced Analytics
                  </div>
                  <div className="flex items-center">                    <Badge variant={safeLimits?.apiAccess ? 'default' : 'secondary'} className="mr-2">
                      {safeLimits?.apiAccess ? '✓' : '✗'}
                    </Badge>
                    API Access
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upgrade" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => (              <Card key={plan.tier} className={`relative ${safeTier === plan.tier ? 'ring-2 ring-blue-500' : ''}`}>
                {safeTier === plan.tier && (
                  <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-blue-500">
                    Current Plan
                  </Badge>
                )}
                <CardHeader className="" hasCardAction={false}>
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <CardDescription className="text-2xl font-bold">{plan.price}<span className="text-sm font-normal">/month</span></CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <Badge variant="outline" className="mr-2 h-4 w-4 p-0 rounded-full">✓</Badge>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>                  <Button 
                    className="w-full" 
                    disabled={safeTier === plan.tier || upgradeSubscription.loading}
                    variant={safeTier === plan.tier ? 'secondary' : 'default'}
                    onClick={() => handleUpgrade(plan.tier)}
                  >
                    {upgradeSubscription.loading 
                      ? 'Upgrading...' 
                      : safeTier === plan.tier 
                        ? 'Current Plan' 
                        : 'Upgrade'
                    }
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="packages" className="space-y-4">          <Card>
            <CardHeader className="" hasCardAction={false}>
              <CardTitle>Action Packages</CardTitle>
              <CardDescription>
                Purchase additional AI actions for your account
              </CardDescription>
            </CardHeader>            <CardContent>
              {packagesLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>              ) : !safePackages || safePackages.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No action packages available</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {safePackages.map((pkg) => (<Card key={pkg.id} className="relative">
                      <CardHeader className="" hasCardAction={false}>
                        <CardTitle className="text-lg">{pkg.name}</CardTitle>
                        <CardDescription>{pkg.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Actions:</span>
                            <span className="font-medium">{pkg.actionsIncluded.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Price:</span>
                            <span className="font-medium">${pkg.costUsd}</span>
                          </div>
                          {pkg.validityDays && (
                            <div className="flex justify-between">
                              <span>Valid for:</span>
                              <span className="font-medium">{pkg.validityDays} days</span>
                            </div>
                          )}
                          {pkg.isActive && (
                            <Badge variant="default" className="w-full justify-center">
                              Active
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                      <CardFooter>
                        <Button 
                          className="w-full" 
                          disabled={pkg.isActive || purchasePackage.loading}
                          onClick={() => handlePurchasePackage(pkg.id)}
                        >
                          {pkg.isActive ? 'Already Purchased' : 'Purchase'}                        </Button>
                      </CardFooter>
                    </Card>                  ))}                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="action-logs" className="space-y-4">
          <AIActionLogsViewer 
            organizationId={activeTab === 'organization' ? (selectedOrgId || organizationId || user?.organizationId) : undefined}
          />
        </TabsContent>
      </Tabs>
    </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-muted-foreground">
          Manage your subscription plan, monitor usage, and purchase additional action packages.
        </p>
      </div>
        {/* Tabs for Personal vs Organization Subscriptions */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="personal">
            <User className="mr-2 h-4 w-4" />
            Personal
          </TabsTrigger>          <TabsTrigger 
            value="organization" 
            disabled={!(organizationId || user?.organizationId || (organizations && organizations.length > 0))}
          >
            <Building className="mr-2 h-4 w-4" />
            Organization {!(organizationId || user?.organizationId || (organizations && organizations.length > 0)) && '(No org found)'}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="personal" className="space-y-6">
          {renderSubscriptionContent()}
        </TabsContent>        <TabsContent value="organization" className="space-y-6">
          {/* Organization Selector */}
          {organizations && organizations.length > 0 && (
            <Card>
              <CardHeader className="" hasCardAction={false}>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Select Organization
                </CardTitle>
                <CardDescription>
                  Choose an organization to view its subscription details.
                </CardDescription>
              </CardHeader>              <CardContent>
                <Select 
                  value={selectedOrgId || organizationId || user?.organizationId || ''} 
                  onValueChange={setSelectedOrgId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an organization..." />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org: any) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name} {user?.organizationId === org.id && '(Primary)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {(organizationId || user?.organizationId || selectedOrgId) ? (
            renderSubscriptionContent()
          ) : (
            <Card>
              <CardHeader className="" hasCardAction={false}>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Organization Subscription
                </CardTitle>
                <CardDescription>
                  You're not currently part of an organization. Join or create an organization to manage organization subscriptions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Organization subscriptions allow you to:
                </p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 mb-4">
                  <li>Share AI actions and storage across team members</li>
                  <li>Centralized billing and subscription management</li>
                  <li>Advanced collaboration features</li>
                  <li>Team analytics and usage tracking</li>
                </ul>
                <Button 
                  onClick={() => {
                    // This would trigger organization creation/join flow
                    toast({
                      title: 'Organization Management',
                      description: 'Use the Organization section in the dashboard to create or join an organization.',
                    })
                  }}
                  className="w-full"
                >
                  Learn About Organizations
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default SubscriptionManager
